import type { ContentPost } from 'api/new-analytics/types';
import type { ReportPlatformBlock } from 'app/modules/analytics/modules/reports/types/ReportPlatformBlock';
import { REPORT_PREVIEW_CHARTS_PER_ROW } from 'app/modules/analytics/modules/reports/constants/reportPreviewChartLayout';

export interface ReportPreviewPageModel {
  chartMetrics: string[];
  performanceTileMetrics: string[];
  contentMetrics: string[];
  postChunk?: ContentPost[];
  platformBlocks?: ReportPlatformBlock[];
}

// Height model (arbitrary units, page = 100). Values approximate the rendered
// vertical footprint on an A4 preview page so that sections share a page when
// they fit and only break when they genuinely overflow.
export const PREVIEW_PAGE_HEIGHT_CAPACITY = 100;
// Every rendered page also carries fixed chrome the section heights below do
// not include: the repeating profile/report header (the report name is clamped
// to two lines for the export layout so this stays bounded) and the page
// footer. Reserve room for it so a page is never packed beyond what physically
// fits, which would otherwise overflow onto the next physical page and split a
// section across the break.
export const PREVIEW_PAGE_CHROME_HEIGHT = 18;
export const PREVIEW_PAGE_CONTENT_CAPACITY =
  PREVIEW_PAGE_HEIGHT_CAPACITY - PREVIEW_PAGE_CHROME_HEIGHT;
export const PREVIEW_SECTION_HEADER_HEIGHT = 6;
export const PREVIEW_SECTION_GAP_HEIGHT = 4;
// A rendered metric-card row (label + value + padding) is tall; under-sizing it
// lets the packer place more rows on a page than physically fit, so the section
// overflows onto a headerless continuation page. Keep this calibrated to the
// real card height so oversized sections split into their own headed pages.
export const PREVIEW_TILE_ROW_HEIGHT = 16;
// Calibrated for full-width chart cards (160px chart + header/legend) in a
// 1-column layout. Three charts plus the performance section header fit within
// PREVIEW_PAGE_CONTENT_CAPACITY.
export const PREVIEW_CHART_HEIGHT = 25;
export const PREVIEW_CHARTS_PER_ROW = REPORT_PREVIEW_CHARTS_PER_ROW;
export const PREVIEW_POST_TABLE_HEADER_HEIGHT = 8;
export const PREVIEW_POST_ROW_HEIGHT = 5;
export const PREVIEW_TILES_PER_ROW = 2;

type MetricField = 'chartMetrics' | 'performanceTileMetrics' | 'contentMetrics';

export function createEmptyReportPreviewPage(): ReportPreviewPageModel {
  return {
    chartMetrics: [],
    performanceTileMetrics: [],
    contentMetrics: [],
  };
}

function createEmptyPage(): ReportPreviewPageModel {
  return createEmptyReportPreviewPage();
}

function filterReportPreviewPages(
  pages: ReportPreviewPageModel[],
  keepNoteOnlyFallback: boolean,
): ReportPreviewPageModel[] {
  const filtered = pages.filter(pageHasContent);

  if (filtered.length === 0 && keepNoteOnlyFallback) {
    return [createEmptyReportPreviewPage()];
  }

  return filtered;
}

function pageHasContent(page: ReportPreviewPageModel): boolean {
  return (
    page.chartMetrics.length > 0 ||
    page.performanceTileMetrics.length > 0 ||
    page.contentMetrics.length > 0 ||
    (page.postChunk?.length ?? 0) > 0 ||
    (page.platformBlocks?.length ?? 0) > 0
  );
}

export function reportPreviewPageHasContent(
  page: ReportPreviewPageModel,
): boolean {
  return pageHasContent(page);
}

function getTileRowsHeight(tileCount: number): number {
  return Math.ceil(tileCount / PREVIEW_TILES_PER_ROW) * PREVIEW_TILE_ROW_HEIGHT;
}

function getChartRowsHeight(chartCount: number): number {
  if (chartCount === 0) {
    return 0;
  }

  return Math.ceil(chartCount / PREVIEW_CHARTS_PER_ROW) * PREVIEW_CHART_HEIGHT;
}

function getPageHeight(page: ReportPreviewPageModel): number {
  let height = 0;

  const hasPerformance =
    page.chartMetrics.length > 0 || page.performanceTileMetrics.length > 0;
  if (hasPerformance) {
    height +=
      PREVIEW_SECTION_HEADER_HEIGHT +
      getTileRowsHeight(page.performanceTileMetrics.length) +
      getChartRowsHeight(page.chartMetrics.length);
  }

  if (page.contentMetrics.length > 0) {
    if (height > 0) {
      height += PREVIEW_SECTION_GAP_HEIGHT;
    }
    height +=
      PREVIEW_SECTION_HEADER_HEIGHT +
      getTileRowsHeight(page.contentMetrics.length);
  }

  const postCount = page.postChunk?.length ?? 0;
  if (postCount > 0) {
    if (height > 0) {
      height += PREVIEW_SECTION_GAP_HEIGHT;
    }
    height +=
      PREVIEW_POST_TABLE_HEADER_HEIGHT + postCount * PREVIEW_POST_ROW_HEIGHT;
  }

  return height;
}

function packMetricField(
  pages: ReportPreviewPageModel[],
  items: string[],
  field: MetricField,
): void {
  for (const item of items) {
    let page = pages[pages.length - 1];
    page[field].push(item);

    if (getPageHeight(page) > PREVIEW_PAGE_CONTENT_CAPACITY) {
      page[field].pop();
      if (pageHasContent(page)) {
        pages.push(createEmptyPage());
        page = pages[pages.length - 1];
      }
      page[field].push(item);
    }
  }
}

function packSingleFieldPages(
  items: string[],
  field: MetricField,
): ReportPreviewPageModel[] {
  if (items.length === 0) {
    return [];
  }

  const pages: ReportPreviewPageModel[] = [createEmptyPage()];
  packMetricField(pages, items, field);

  return pages.filter(pageHasContent);
}

function pageFitsAllItems(
  page: ReportPreviewPageModel,
  patch: Partial<ReportPreviewPageModel>,
): boolean {
  const candidate: ReportPreviewPageModel = {
    ...page,
    ...patch,
    chartMetrics: patch.chartMetrics ?? page.chartMetrics,
    performanceTileMetrics:
      patch.performanceTileMetrics ?? page.performanceTileMetrics,
    contentMetrics: patch.contentMetrics ?? page.contentMetrics,
    postChunk: patch.postChunk ?? page.postChunk,
    platformBlocks: patch.platformBlocks ?? page.platformBlocks,
  };

  return getPageHeight(candidate) <= PREVIEW_PAGE_CONTENT_CAPACITY;
}

function appendGroupedField(
  basePages: ReportPreviewPageModel[],
  items: string[],
  field: MetricField,
): ReportPreviewPageModel[] {
  if (items.length === 0) {
    return basePages;
  }

  const groupPages = packSingleFieldPages(items, field);

  if (basePages.length === 0) {
    return groupPages;
  }

  const lastPage = basePages[basePages.length - 1];
  const patch = { [field]: items } as Partial<ReportPreviewPageModel>;

  if (pageFitsAllItems(lastPage, patch)) {
    lastPage[field] = [...items];
    return basePages;
  }

  return [...basePages, ...groupPages];
}

function packPerformancePages(
  performanceTileMetrics: string[],
  chartMetrics: string[],
): ReportPreviewPageModel[] {
  const tilePages = packSingleFieldPages(
    performanceTileMetrics,
    'performanceTileMetrics',
  );

  if (chartMetrics.length === 0) {
    return tilePages;
  }

  return appendGroupedField(tilePages, chartMetrics, 'chartMetrics');
}

function packContentPages(
  basePages: ReportPreviewPageModel[],
  contentMetrics: string[],
): ReportPreviewPageModel[] {
  return appendGroupedField(basePages, contentMetrics, 'contentMetrics');
}

function packSinglePostGroupPages(
  posts: ContentPost[],
): ReportPreviewPageModel[] {
  const pages: ReportPreviewPageModel[] = [createEmptyPage()];
  packPosts(pages, posts);

  return pages.filter(pageHasContent);
}

function appendGroupedPosts(
  basePages: ReportPreviewPageModel[],
  posts: ContentPost[],
): ReportPreviewPageModel[] {
  if (posts.length === 0) {
    return basePages;
  }

  if (basePages.length === 0) {
    return packSinglePostGroupPages(posts);
  }

  const lastPage = basePages[basePages.length - 1];

  if (pageFitsAllItems(lastPage, { postChunk: [...posts] })) {
    lastPage.postChunk = [...posts];
    return basePages;
  }

  return [...basePages, ...packSinglePostGroupPages(posts)];
}

export function buildReportPreviewPages(input: {
  chartMetrics: string[];
  performanceTileMetrics: string[];
  contentMetrics: string[];
}): ReportPreviewPageModel[] {
  const hasAnyMetrics =
    input.chartMetrics.length > 0 ||
    input.performanceTileMetrics.length > 0 ||
    input.contentMetrics.length > 0;

  if (!hasAnyMetrics) {
    return [];
  }

  const pages = packContentPages(
    packPerformancePages(input.performanceTileMetrics, input.chartMetrics),
    input.contentMetrics,
  );

  return pages.filter(pageHasContent);
}

function packPosts(
  pages: ReportPreviewPageModel[],
  posts: ContentPost[],
): void {
  if (posts.length === 0) {
    return;
  }

  if (pages.length === 0) {
    pages.push(createEmptyPage());
  }

  for (const post of posts) {
    let page = pages[pages.length - 1];
    if (!page.postChunk) {
      page.postChunk = [];
    }
    page.postChunk.push(post);

    if (getPageHeight(page) > PREVIEW_PAGE_CONTENT_CAPACITY) {
      page.postChunk.pop();
      if (pageHasContent(page)) {
        pages.push(createEmptyPage());
        page = pages[pages.length - 1];
        page.postChunk = [];
      }
      page.postChunk.push(post);
    }
  }
}

export function buildReportPreviewPagesForCard(input: {
  chartMetrics: string[];
  performanceTileMetrics: string[];
  contentMetrics: string[];
  posts?: ContentPost[];
  includePosts?: boolean;
  performanceNote?: string | null;
  contentNote?: string | null;
}): ReportPreviewPageModel[] {
  const keepNoteOnlyFallback =
    Boolean(input.performanceNote?.trim()) ||
    Boolean(input.contentNote?.trim());

  let pages = buildReportPreviewPages({
    chartMetrics: input.chartMetrics,
    performanceTileMetrics: input.performanceTileMetrics,
    contentMetrics: input.contentMetrics,
  });

  if (pages.length === 0 && keepNoteOnlyFallback) {
    pages = [createEmptyReportPreviewPage()];
  }

  if (input.includePosts && input.posts && input.posts.length > 0) {
    pages = appendGroupedPosts(pages, input.posts);
  }

  return filterReportPreviewPages(pages, keepNoteOnlyFallback);
}
